/**
 * Planet faction themes — enemies + allied ships look different per world type.
 * Visuals, spawn weights, mild combat flavor. Danger still scales with planet.danger.
 *
 * Variants: swarm, brute, spiker, orbiter, interceptor, bomber, phantom, saucer, mine
 */

/** @typedef {{ colors: string[], bossColor: string, variants: Record<string,number>, form: string, enemyShip: string, speedMul: number, hpMul: number, dmgMul: number, label: string, playerPrimary: string, playerSecondary: string, shipStyle: string, botPalette: string[] }} FactionTheme */

/** @type {Record<string, FactionTheme>} */
export const FACTIONS = {
    terran: {
        label: 'Verdant Patrol',
        colors: ['#3dff88', '#22cc66', '#88ffaa', '#00aa55'],
        bossColor: '#00ff99',
        variants: { swarm: 3, brute: 1, spiker: 1, orbiter: 2, interceptor: 2, bomber: 1, saucer: 1 },
        form: 'organic',
        enemyShip: 'drone',
        speedMul: 1.05,
        hpMul: 1.0,
        dmgMul: 0.95,
        playerPrimary: '#3dff88',
        playerSecondary: '#aaffcc',
        shipStyle: 'dart',
        botPalette: ['#3dff88', '#66ffaa', '#22cc66', '#00dd77'],
    },
    desert: {
        label: 'Sand Corsairs',
        colors: ['#ffcc44', '#e8a020', '#ff8844', '#c47820'],
        bossColor: '#ffaa00',
        variants: { swarm: 2, brute: 3, spiker: 2, orbiter: 1, bomber: 2, mine: 2, interceptor: 1 },
        form: 'scrap',
        enemyShip: 'scavenger',
        speedMul: 0.95,
        hpMul: 1.15,
        dmgMul: 1.05,
        playerPrimary: '#ffcc44',
        playerSecondary: '#ff8844',
        shipStyle: 'wedge',
        botPalette: ['#ffcc44', '#e8a020', '#ffaa33', '#dd8822'],
    },
    ice: {
        label: 'Cryo Flotilla',
        colors: ['#88ddff', '#44aadd', '#cceeff', '#66bbff'],
        bossColor: '#66eeff',
        variants: { swarm: 2, brute: 1, spiker: 1, orbiter: 3, saucer: 2, phantom: 2, interceptor: 1 },
        form: 'crystal',
        enemyShip: 'shard',
        speedMul: 0.9,
        hpMul: 1.1,
        dmgMul: 1.0,
        playerPrimary: '#88ddff',
        playerSecondary: '#e0f8ff',
        shipStyle: 'diamond',
        botPalette: ['#88ddff', '#66bbff', '#aadfff', '#44ccee'],
    },
    lava: {
        label: 'Infernal Swarm',
        colors: ['#ff4422', '#ff8800', '#ff6644', '#cc2200'],
        bossColor: '#ff2200',
        variants: { swarm: 2, brute: 2, spiker: 3, orbiter: 1, mine: 2, bomber: 2, interceptor: 1 },
        form: 'ember',
        enemyShip: 'blade',
        speedMul: 1.1,
        hpMul: 0.95,
        dmgMul: 1.2,
        playerPrimary: '#ff5533',
        playerSecondary: '#ffcc44',
        shipStyle: 'blade',
        botPalette: ['#ff4422', '#ff8800', '#ff6644', '#ee3311'],
    },
    gas: {
        label: 'Gas Harvesters',
        colors: ['#e8c070', '#c99850', '#ffd090', '#aa7740'],
        bossColor: '#ffcc66',
        variants: { swarm: 1, brute: 2, spiker: 1, orbiter: 3, saucer: 3, bomber: 2, phantom: 1 },
        form: 'gaseous',
        enemyShip: 'ring',
        speedMul: 0.85,
        hpMul: 1.2,
        dmgMul: 0.9,
        playerPrimary: '#e8c070',
        playerSecondary: '#ffe8b0',
        shipStyle: 'ring',
        botPalette: ['#e8c070', '#c99850', '#ffd090', '#ddaa55'],
    },
    toxic: {
        label: 'Biohazard Hive',
        colors: ['#88ff22', '#66cc00', '#aaff44', '#44aa00'],
        bossColor: '#aaff00',
        variants: { swarm: 4, brute: 1, spiker: 2, orbiter: 1, mine: 3, phantom: 2, interceptor: 1 },
        form: 'toxic',
        enemyShip: 'spiker',
        speedMul: 1.15,
        hpMul: 0.9,
        dmgMul: 1.15,
        playerPrimary: '#88ff22',
        playerSecondary: '#ccff66',
        shipStyle: 'spiker',
        botPalette: ['#88ff22', '#66cc00', '#aaff44', '#55dd11'],
    },
    ocean: {
        label: 'Abyssal Navy',
        colors: ['#2288ff', '#44aadd', '#66ccff', '#0066cc'],
        bossColor: '#00aaff',
        variants: { swarm: 2, brute: 1, spiker: 2, orbiter: 2, saucer: 2, interceptor: 2, bomber: 1 },
        form: 'aquatic',
        enemyShip: 'fin',
        speedMul: 1.0,
        hpMul: 1.05,
        dmgMul: 1.0,
        playerPrimary: '#33aaff',
        playerSecondary: '#88eeff',
        shipStyle: 'fin',
        botPalette: ['#2288ff', '#44aadd', '#66ccff', '#1199ee'],
    },
    storm: {
        label: 'Void Storm',
        colors: ['#aa66ff', '#7744dd', '#cc99ff', '#5522aa'],
        bossColor: '#cc44ff',
        variants: { swarm: 2, brute: 1, spiker: 2, orbiter: 2, phantom: 3, interceptor: 3, saucer: 1 },
        form: 'storm',
        enemyShip: 'bolt',
        speedMul: 1.2,
        hpMul: 0.95,
        dmgMul: 1.1,
        playerPrimary: '#aa66ff',
        playerSecondary: '#ff88ff',
        shipStyle: 'bolt',
        botPalette: ['#aa66ff', '#7744dd', '#cc99ff', '#9966ee'],
    },
    crystal: {
        label: 'Prism Legion',
        colors: ['#66eeff', '#ff66cc', '#88ccff', '#ffaaff'],
        bossColor: '#ff66ee',
        variants: { swarm: 1, brute: 2, spiker: 2, orbiter: 2, saucer: 2, phantom: 2, bomber: 1 },
        form: 'prism',
        enemyShip: 'prism',
        speedMul: 1.0,
        hpMul: 1.1,
        dmgMul: 1.05,
        playerPrimary: '#66eeff',
        playerSecondary: '#ff66cc',
        shipStyle: 'prism',
        botPalette: ['#66eeff', '#ff66cc', '#88ccff', '#ee88ff'],
    },
    barren: {
        label: 'Scrap Raiders',
        colors: ['#aabbcc', '#8899aa', '#ccddee', '#667788'],
        bossColor: '#ddeeff',
        variants: { swarm: 2, brute: 3, spiker: 2, orbiter: 1, mine: 2, bomber: 2, interceptor: 1 },
        form: 'scrap',
        enemyShip: 'block',
        speedMul: 0.95,
        hpMul: 1.25,
        dmgMul: 1.0,
        playerPrimary: '#aabbcc',
        playerSecondary: '#667788',
        shipStyle: 'block',
        botPalette: ['#aabbcc', '#8899aa', '#ccddee', '#99aabb'],
    },
    jungle: {
        label: 'Canopy Swarm',
        colors: ['#33cc55', '#22aa44', '#66ff88', '#118833'],
        bossColor: '#22ff66',
        variants: { swarm: 4, brute: 2, spiker: 2, orbiter: 1, mine: 2, interceptor: 2, phantom: 1 },
        form: 'organic',
        enemyShip: 'leaf',
        speedMul: 1.1,
        hpMul: 1.0,
        dmgMul: 1.0,
        playerPrimary: '#33cc55',
        playerSecondary: '#aaff88',
        shipStyle: 'leaf',
        botPalette: ['#33cc55', '#22aa44', '#66ff88', '#44dd66'],
    },
    metal: {
        label: 'Mech Legion',
        colors: ['#88aacc', '#6688aa', '#aaccee', '#446688'],
        bossColor: '#99ccff',
        variants: { swarm: 1, brute: 3, spiker: 1, orbiter: 2, bomber: 2, saucer: 2, interceptor: 1 },
        form: 'mech',
        enemyShip: 'mech',
        speedMul: 0.85,
        hpMul: 1.35,
        dmgMul: 1.1,
        playerPrimary: '#88aacc',
        playerSecondary: '#ccddee',
        shipStyle: 'mech',
        botPalette: ['#88aacc', '#6688aa', '#aaccee', '#7799bb'],
    },
};

const DEFAULT_FACTION = FACTIONS.barren;

/**
 * Resolve faction theme from a planet (or type string).
 * @param {{ type?: string }|string|null|undefined} planetOrType
 * @returns {FactionTheme}
 */
export function getFaction(planetOrType) {
    const type = typeof planetOrType === 'string'
        ? planetOrType
        : planetOrType?.type;
    return (type && FACTIONS[type]) || DEFAULT_FACTION;
}

/**
 * Weighted variant pick from faction spawn table.
 * @param {FactionTheme} faction
 * @returns {string}
 */
export function pickEnemyVariant(faction) {
    const table = faction?.variants || DEFAULT_FACTION.variants;
    let total = 0;
    for (const w of Object.values(table)) total += w;
    let r = Math.random() * total;
    for (const [v, w] of Object.entries(table)) {
        r -= w;
        if (r <= 0) return v;
    }
    return 'swarm';
}

/**
 * Random enemy color from faction palette.
 * @param {FactionTheme} faction
 */
export function pickEnemyColor(faction) {
    const colors = faction?.colors || DEFAULT_FACTION.colors;
    return colors[Math.floor(Math.random() * colors.length)];
}

/**
 * Mild danger scaling stacked on faction multipliers.
 * @param {FactionTheme} faction
 * @param {number} danger 0..1
 */
export function combatScale(faction, danger = 0.5) {
    const d = 0.85 + (danger || 0.5) * 0.4; // ~0.85–1.25
    return {
        speedMul: (faction?.speedMul ?? 1) * (0.95 + (danger || 0.5) * 0.15),
        hpMul: (faction?.hpMul ?? 1) * d,
        dmgMul: (faction?.dmgMul ?? 1) * d,
    };
}
